import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Loader2, Package, DollarSign, TrendingUp, Percent, BarChart } from "lucide-react";
import { toastApiError } from "../../lib/toastApiError";

export default function StockValue() {
  const { t, i18n } = useTranslation("pharmacy");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const numberLocale = i18n.language === "en" ? "en-US" : "fr-FR";

  const fetchStockValue = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/pharmacy/stock-value");
      setData(res.data);
    } catch (error) {
      console.error("Error fetching stock value:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockValue();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(numberLocale, {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + " CDF";
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="stock-value-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t("stockValue.title")}</h1>
        <p className="text-slate-500 mt-1">{t("stockValue.subtitle")}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Package className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("stockValue.items")}</p>
                    <p className="text-2xl font-bold text-blue-600">{data.items_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <BarChart className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("stockValue.totalQuantity")}</p>
                    <p className="text-2xl font-bold text-purple-600">{data.total_quantity}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("stockValue.purchaseValue")}</p>
                    <p className="text-xl font-bold text-amber-600">{formatCurrency(data.total_purchase_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("stockValue.saleValue")}</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(data.total_selling_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-800">
                <TrendingUp className="h-5 w-5" />
                {t("stockValue.potentialProfit")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-green-700">{t("stockValue.profitIfSold")}</p>
                  <p className="text-4xl font-bold text-green-600 mt-2">{formatCurrency(data.potential_profit)}</p>
                </div>
                <div className="flex items-center">
                  <div className="p-4 bg-white/50 rounded-lg flex items-center gap-3">
                    <Percent className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="text-sm text-green-700">{t("stockValue.profitMargin")}</p>
                      <p className="text-3xl font-bold text-green-600">{data.profit_margin}%</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("stockValue.financialSummary")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-slate-600">{t("stockValue.totalPurchaseCost")}</span>
                  <span className="font-semibold text-amber-600">{formatCurrency(data.total_purchase_value)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-slate-600">{t("stockValue.totalSaleValue")}</span>
                  <span className="font-semibold text-green-600">{formatCurrency(data.total_selling_value)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-slate-600">{t("stockValue.potentialProfitLabel")}</span>
                  <span className="font-bold text-green-600">{formatCurrency(data.potential_profit)}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-600">{t("stockValue.averageMargin")}</span>
                  <span className="font-bold text-omu-blue-600">{data.profit_margin}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {data.profit_margin < 20 && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900">{t("stockValue.tip")}</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      {t("stockValue.tipLowMargin", { margin: data.profit_margin })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          {t("stockValue.noData")}
        </div>
      )}
    </div>
  );
}
